import {useRef, useState, type ReactNode} from "react";
import {MagnifyingGlassIcon} from "@heroicons/react/24/outline";
import {cn} from "../../../../../lib/utils.ts";
import {AccountNature, BookingFilter, type BookingFilterCounts} from "../types.ts";
import {visibleFilterChips} from "../utils/index.ts";

interface FilterChipsProps {
    activeFilter: BookingFilter;
    counts: BookingFilterCounts;
    nature: AccountNature;
    searchQuery: string;
    onSearchChange: (query: string) => void;
    onFilterChange: (filter: BookingFilter) => void;
    leading?: ReactNode;
    actions?: ReactNode;
}

export function FilterChips({
    activeFilter,
    counts,
    nature,
    searchQuery,
    onSearchChange,
    onFilterChange,
    leading,
    actions,
}: FilterChipsProps) {
    const chips = visibleFilterChips(counts, nature);

    return (
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 pb-3">
            <div className="flex flex-wrap items-center gap-2">
                {chips.map(({filter, label, count}) => (
                    <button
                        key={filter}
                        type="button"
                        onClick={() => onFilterChange(filter)}
                        className={cn(
                            "rounded-full px-3 py-1 text-sm font-medium ring-1 ring-inset transition",
                            activeFilter === filter
                                ? "bg-primary text-white ring-primary"
                                : "bg-white text-foreground ring-border hover:bg-muted",
                        )}
                    >
                        {label} {count}
                    </button>
                ))}
            </div>
            <div className="flex items-center gap-1">
                {leading}
                <SearchField value={searchQuery} onChange={onSearchChange} />
                {actions}
            </div>
        </div>
    );
}

function SearchField({value, onChange}: {value: string; onChange: (query: string) => void}) {
    const [open, setOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const expanded = open || value.length > 0;

    return (
        <div className="flex items-center">
            <button
                type="button"
                onClick={() => {
                    setOpen(true);
                    inputRef.current?.focus();
                }}
                className="flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                aria-label="Zoeken"
            >
                <MagnifyingGlassIcon className="size-5" aria-hidden />
            </button>
            <input
                ref={inputRef}
                type="text"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onBlur={() => {
                    if (value.length === 0) setOpen(false);
                }}
                placeholder="Zoeken..."
                tabIndex={expanded ? 0 : -1}
                className={cn(
                    "border-0 bg-transparent p-0 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0",
                    "transition-all duration-200 ease-out",
                    expanded ? "ml-1 w-44 opacity-100" : "w-0 opacity-0",
                )}
            />
        </div>
    );
}
